import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Ticket, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && !user.must_change_password) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      if (u.must_change_password) nav("/change-password");
      else nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between p-10 bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-600 text-white relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-16 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/15 grid place-items-center">
              <Ticket className="w-6 h-6" />
            </div>
            <div>
              <div className="font-display font-bold text-2xl leading-none">Job Ticker</div>
              <div className="text-xs text-white/70">Facility Ops Ticketing</div>
            </div>
          </div>
        </div>
        <div className="relative space-y-6 max-w-md">
          <h1 className="font-display font-bold text-4xl leading-tight">Every outlet issue,<br/>tracked to resolution.</h1>
          <p className="text-white/80 text-sm leading-relaxed">
            Raise, resolve and audit facility work across every outlet — with photo proof, payment logs
            and a Health Board that shows exactly where trouble is stacking up.
          </p>
          <div className="flex items-center gap-4 text-xs text-white/70">
            <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-300"/>Photo proof</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-amber-300"/>SLA escalation</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-sky-300"/>Payment ledger</span>
          </div>
        </div>
        <div className="relative text-xs text-white/60">Web + Android WebView • same backend, no data drift</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8 flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-xl bg-indigo-600 grid place-items-center text-white">
              <Ticket className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display font-bold text-slate-900 text-lg leading-none">Job Ticker</div>
              <div className="text-xs text-slate-500">Facility Ops</div>
            </div>
          </div>

          <h2 className="font-display font-bold text-3xl text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1">Use your work email to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                className="mt-1.5 h-11"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="mt-1.5 h-11"
                placeholder="••••••••"
              />
            </div>

            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div data-testid="invite-only-note" className="mt-5 text-center text-xs text-slate-500">
            Access is invite-only. Contact your administrator to be added.
          </div>
        </div>
      </div>
    </div>
  );
}
